import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Users, Plus, Trash2, Search, X, MapPin, Star, 
  Trophy, Award, AlertCircle, Loader
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProviderPartners = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    loadPartners();
  }, []);

  const loadPartners = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/provider/partners`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPartners(response.data);
    } catch (error) {
      console.error('Error loading partners:', error);
      toast.error('Fehler beim Laden der Partner');
    } finally {
      setLoading(false);
    }
  };

  const searchProviders = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/provider/partners/search`, {
        params: { query: searchQuery },
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(response.data);
    } catch (error) {
      console.error('Error searching providers:', error);
      toast.error('Fehler bei der Suche');
    } finally {
      setSearching(false);
    }
  };

  const addPartner = async (partnerId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/provider/partners/add`, 
        { partner_provider_id: partnerId },
        { 
          headers: { Authorization: `Bearer ${token}` },
          params: { partner_provider_id: partnerId }
        }
      );
      toast.success('Partner erfolgreich hinzugefügt');
      loadPartners();
      setShowSearchModal(false);
      setSearchQuery('');
      setSearchResults([]);
    } catch (error) {
      // Comprehensive logging for debugging
      console.error('Error adding partner:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        code: error.code
      });

      // HTTP error with response from backend
      if (error.response?.data) {
        const { detail } = error.response.data;

        if (Array.isArray(detail)) {
          // FastAPI validation errors (422)
          const messages = detail.map(e => e.msg).filter(Boolean).join(', ');
          toast.error(messages || 'Validierungsfehler');
          return;
        }

        if (detail && typeof detail === 'object' && detail.message) {
          // Structured error object
          toast.error(detail.message);
          return;
        }

        if (typeof detail === 'string' && detail.length > 0) {
          // Simple string detail (FastAPI HTTPException)
          toast.error(detail);
          return;
        }

        // Unexpected response structure
        toast.error('Fehler beim Hinzufügen');
        return;
      }

      // Network/request errors (no response from server)
      if (error.request) {
        toast.error('Server nicht erreichbar - bitte versuchen Sie es später erneut');
        return;
      }

      // Unknown error
      toast.error('Fehler beim Hinzufügen');
    }
  };

  const removePartner = async (partnershipId) => {
    if (!window.confirm('Möchten Sie diesen Partner wirklich entfernen?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/provider/partners/remove/${partnershipId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Partner entfernt');
      loadPartners();
    } catch (error) {
      console.error('Error removing partner:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        code: error.code
      });

      if (error.response?.data) {
        const { detail } = error.response.data;

        if (Array.isArray(detail)) {
          const messages = detail.map(e => e.msg).filter(Boolean).join(', ');
          toast.error(messages || 'Validierungsfehler');
          return;
        }

        if (detail && typeof detail === 'object' && detail.message) {
          toast.error(detail.message);
          return;
        }

        if (typeof detail === 'string' && detail.length > 0) {
          toast.error(detail);
          return;
        }

        toast.error('Fehler beim Entfernen');
        return;
      }

      if (error.request) {
        toast.error('Server nicht erreichbar - bitte versuchen Sie es später erneut');
        return;
      }

      toast.error('Fehler beim Entfernen');
    }
  };

  const getPlanBadge = (planLevel) => {
    if (planLevel === 'pro') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold rounded-full">
          <Trophy className="w-3 h-3" />
          Top
        </span>
      );
    } else if (planLevel === 'plus') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-[#2B2142] to-[#3D3158] text-white text-xs font-bold rounded-full">
          <Award className="w-3 h-3" />
          Plus
        </span>
      );
    }
    return null;
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchQuery) {
        searchProviders();
      } else {
        setSearchResults([]);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-[#2B2142]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#FFF0D1]/30 to-[#F4F2F2] rounded-2xl p-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Partner & Netzwerk</h2>
            <p className="text-gray-700 max-w-3xl">
              Verlinken Sie andere Dienstleister auf EventConnect, mit denen Sie gerne zusammenarbeiten. 
              So helfen Sie Nutzern, passende Anbieter schneller zu finden – und ihr profitiert gegenseitig 
              von mehr Sichtbarkeit.
            </p>
          </div>
          <button
            onClick={() => setShowSearchModal(true)}
            disabled={partners.length >= 10}
            className="px-6 py-3 bg-[#2B2142] hover:bg-[#1E1730] text-white font-semibold rounded-xl transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-5 h-5" />
            Partner hinzufügen
          </button>
        </div>

        {partners.length >= 10 && (
          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-xl flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-yellow-800">
              Sie haben das Maximum von 10 Partnern erreicht. Entfernen Sie zuerst einen Partner, 
              um einen neuen hinzuzufügen.
            </p>
          </div>
        )}
      </div>

      {/* Partner List */}
      <div>
        <h3 className="text-lg font-bold text-gray-900 mb-4">
          Meine Partner ({partners.length}/10)
        </h3>

        {partners.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-2xl">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">Noch keine Partner hinzugefügt</p>
            <button
              onClick={() => setShowSearchModal(true)}
              className="px-6 py-3 bg-[#2B2142] hover:bg-[#1E1730] text-white font-semibold rounded-xl transition-colors inline-flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Ersten Partner hinzufügen
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {partners.map((partner) => (
              <div
                key={partner.id}
                className="bg-white rounded-xl shadow-sm border-2 border-gray-100 p-5 hover:border-[#2B2142]/20 transition-colors"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-900 mb-1">{partner.company_name}</h4>
                    {getPlanBadge(partner.plan_level)}
                  </div>
                  <button
                    onClick={() => removePartner(partner.partnership_id)}
                    className="text-red-600 hover:text-red-700 p-2 hover:bg-red-50 rounded-lg transition-colors"
                    title="Partner entfernen"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>

                {partner.categories && partner.categories.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {partner.categories.slice(0, 2).map((cat, idx) => (
                      <span key={idx} className="px-2 py-1 bg-[#FFF0D1]/30 text-[#2B2142] rounded text-xs font-semibold">
                        {cat}
                      </span>
                    ))}
                  </div>
                )}

                {partner.location_city && (
                  <div className="flex items-center gap-1 text-sm text-gray-600 mb-3">
                    <MapPin className="w-4 h-4" />
                    <span>{partner.location_city}</span>
                  </div>
                )}

                {partner.average_rating > 0 && (
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    <span className="font-semibold text-gray-900">{partner.average_rating.toFixed(1)}</span>
                    {partner.review_count > 0 && (
                      <span className="text-sm text-gray-500">({partner.review_count})</span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Search Modal */}
      {showSearchModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">Partner hinzufügen</h3>
                <button
                  onClick={() => {
                    setShowSearchModal(false);
                    setSearchQuery('');
                    setSearchResults([]);
                  }}
                  className="text-gray-400 hover:text-gray-600 p-2"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Search Bar */}
              <div className="relative">
                <Search className="hidden sm:block absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Dienstleister suchen (Name, Firma)..."
                  className="w-full pl-4 sm:pl-12 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#2B2142] focus:outline-none"
                  autoFocus
                />
              </div>
            </div>

            {/* Results */}
            <div className="flex-1 overflow-y-auto p-6">
              {searching ? (
                <div className="flex items-center justify-center py-12">
                  <Loader className="w-8 h-8 animate-spin text-[#2B2142]" />
                </div>
              ) : searchResults.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  {searchQuery ? 'Keine Ergebnisse gefunden' : 'Geben Sie einen Suchbegriff ein'}
                </div>
              ) : (
                <div className="space-y-3">
                  {searchResults.map((provider) => (
                    <div
                      key={provider.id}
                      className="bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="font-bold text-gray-900">{provider.company_name}</h4>
                            {getPlanBadge(provider.plan_level)}
                          </div>

                          {provider.categories && provider.categories.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-2">
                              {provider.categories.slice(0, 3).map((cat, idx) => (
                                <span key={idx} className="px-2 py-0.5 bg-[#FFF0D1] text-[#2B2142] rounded text-xs">
                                  {cat}
                                </span>
                              ))}
                            </div>
                          )}

                          {provider.location_city && (
                            <div className="flex items-center gap-1 text-sm text-gray-600">
                              <MapPin className="w-4 h-4" />
                              <span>{provider.location_city}</span>
                            </div>
                          )}
                        </div>

                        <button
                          onClick={() => addPartner(provider.user_id)}
                          className="px-4 py-2 bg-[#2B2142] hover:bg-[#1E1730] text-white font-semibold rounded-lg transition-colors text-sm"
                        >
                          Hinzufügen
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProviderPartners;
